"use client";

// Falling (snow, semlor, kanelbullar, …) or rising (hearts, pi) particles for
// högtidsteman. Pure CSS animation (fall, sway and spin on three nested
// layers, transform only), so the browser runs it off the main thread: no
// JavaScript per frame at all. A particle can be clicked (round hit area of
// its own size only): it hides, a short CSS burst plays where it was
// (Klick.tsx), and it comes back a few seconds later.

import { useMemo, useState, type CSSProperties } from "react";
import type { HogtidEffekt } from "@/lib/hogtider";
import { PARTIKELFARGER, Partikel } from "./figurer";
import { UtbrottVy, skapaUtbrott, type Utbrott } from "./Klick";
import styles from "./hogtid.module.css";

export type FallandeEffekt = Exclude<HogtidEffekt, "spindlar" | "kycklingar">;

interface Instellning {
  antal: number;
  /** +1 falls, −1 rises. */
  riktning: 1 | -1;
  fart: [number, number]; // px / s (on a 900 px tall screen)
  storlek: [number, number]; // px
  svaj: number; // sideways amplitude, px
  snurr: number; // max rotation speed, deg / s
  /** Rocks back and forth instead of spinning. */
  vagga?: boolean;
  opacitet: number;
  /** Tooltip: what a click does. */
  titel: string;
}

const INSTALLNINGAR: Record<FallandeEffekt, Instellning> = {
  sno: { antal: 14, riktning: 1, fart: [18, 42], storlek: [11, 19], svaj: 22, snurr: 30, opacitet: 0.85, titel: "Blås bort snöflingan" },
  hjartan: { antal: 6, riktning: -1, fart: [20, 38], storlek: [16, 26], svaj: 18, snurr: 0, opacitet: 0.85, titel: "Lös upp hjärtat" },
  pi: { antal: 10, riktning: -1, fart: [12, 24], storlek: [24, 38], svaj: 16, snurr: 12, opacitet: 0.75, titel: "Visa decimalerna" },
  semlor: { antal: 8, riktning: 1, fart: [26, 44], storlek: [26, 36], svaj: 12, snurr: 40, opacitet: 0.95, titel: "Smula sönder semlan" },
  kanelbullar: { antal: 8, riktning: 1, fart: [26, 44], storlek: [24, 32], svaj: 12, snurr: 60, opacitet: 0.95, titel: "Smula sönder kanelbullen" },
  kladdkakor: { antal: 8, riktning: 1, fart: [26, 44], storlek: [26, 34], svaj: 12, snurr: 40, opacitet: 0.95, titel: "Smula sönder kladdkakan" },
  lussekatter: { antal: 8, riktning: 1, fart: [26, 44], storlek: [26, 34], svaj: 12, snurr: 50, opacitet: 0.95, titel: "Smula sönder lussekatten" },
  blommor: { antal: 12, riktning: 1, fart: [18, 32], storlek: [18, 26], svaj: 30, snurr: 50, opacitet: 0.9, titel: "Plocka isär blomman" },
  flaggor: { antal: 10, riktning: 1, fart: [22, 36], storlek: [22, 30], svaj: 26, snurr: 0, vagga: true, opacitet: 0.95, titel: "Skicka iväg flaggan" },
};

/** A clicked particle stays away this long before it shows again. */
const BORTA_MS = 4000;

/** Fixed per particle (derived, so rendering stays pure). */
interface P {
  vanster: number; // % of the screen width
  fallS: number; // seconds for one pass over the screen
  start: number; // 0–1: where in its pass it is at load
  storlek: number;
  svajS: number;
  snurrS: number;
  snurrBak: boolean;
  farg: string;
}

/** Deterministic 0–1 noise per (particle, property): pure, so it can be used
 *  while rendering (React must be able to render twice with the same result). */
function brus(i: number, k: number): number {
  const v = Math.sin(i * 12.9898 + k * 78.233) * 43758.5453;
  return v - Math.floor(v);
}
const mellan = ([a, b]: [number, number], t: number) => a + t * (b - a);

function dolTillfalligt(el: HTMLElement): void {
  el.style.visibility = "hidden";
  window.setTimeout(() => {
    el.style.visibility = "";
  }, BORTA_MS);
}

export default function Fallande({ effekt }: { effekt: FallandeEffekt }) {
  const inst = INSTALLNINGAR[effekt];
  const [utbrott, setUtbrott] = useState<Utbrott[]>([]);

  const partiklar = useMemo<P[]>(() => {
    const farger = PARTIKELFARGER[effekt];
    return Array.from({ length: inst.antal }, (_, i) => {
      const fart = mellan(inst.fart, brus(i, 1));
      const snurr = Math.max(4, brus(i, 6) * inst.snurr);
      return {
        vanster: brus(i, 8) * 96 + 1,
        fallS: 900 / fart,
        start: brus(i, 3),
        storlek: mellan(inst.storlek, brus(i, 2)),
        svajS: 1.6 + brus(i, 4) * 2.2,
        snurrS: 360 / snurr,
        snurrBak: brus(i, 5) < 0.5,
        farg: farger[Math.floor(brus(i, 7) * farger.length)],
      };
    });
  }, [effekt, inst]);

  const klick = (i: number, el: HTMLElement) => {
    const p = partiklar[i];
    const r = el.getBoundingClientRect();
    dolTillfalligt(el);
    const u = skapaUtbrott(effekt, r.left + r.width / 2, r.top + r.height / 2, p.storlek, p.farg);
    setUtbrott((alla) => [...alla, u]);
    window.setTimeout(() => setUtbrott((alla) => alla.filter((a) => a.id !== u.id)), u.langd + 50);
  };

  return (
    <>
      {partiklar.map((p, i) => (
        <div
          key={i}
          className={inst.riktning === 1 ? styles.fall : styles.stig}
          style={
            {
              left: `${p.vanster}%`,
              "--storlek": `${p.storlek}px`,
              animationDuration: `${p.fallS}s`,
              animationDelay: `${-p.start * p.fallS}s`,
            } as CSSProperties
          }
        >
          <div className={styles.svaj} style={{ "--svaj": `${inst.svaj}px`, animationDuration: `${p.svajS}s`, animationDelay: `${-p.start * p.svajS * 2}s` } as CSSProperties}>
            <div
              className={`${styles.partikel} ${inst.vagga ? styles.vagga : inst.snurr ? styles.snurra : ""}`}
              title={inst.titel}
              onClick={(e) => {
                e.stopPropagation();
                klick(i, e.currentTarget);
              }}
              style={{
                width: p.storlek,
                height: p.storlek,
                opacity: inst.opacitet,
                animationDuration: `${inst.vagga ? p.svajS : p.snurrS}s`,
                animationDirection: inst.vagga ? "alternate" : p.snurrBak ? "reverse" : "normal",
              }}
            >
              <svg viewBox="0 0 24 24" width={p.storlek} height={p.storlek}>
                <Partikel effekt={effekt} farg={p.farg} />
              </svg>
            </div>
          </div>
        </div>
      ))}
      {utbrott.map((u) => (
        <UtbrottVy key={u.id} utbrott={u} />
      ))}
    </>
  );
}
